import React from 'react';
import { Link } from 'react-router-dom';
import { Users, Calendar, MapPin, ArrowRight } from 'lucide-react';
import PageHero from '../../components/PageHero';
import CTASection from '../../components/CTASection';
import { COMPANY } from '../../config/siteContent';
import useDocumentTitle from '../../hooks/useDocumentTitle';

const About = () => {
  useDocumentTitle({
    title: 'About Us',
    description:
      'iConnect was established in 2000 in Ann Arbor, Michigan. We are run by EDI veterans from Harbinger, Premenos, Sterling Commerce, and Supply Tech, and process tens of thousands of EDI documents daily.',
  });

  const stats = [
    { Icon: Calendar, label: 'Founded', value: COMPANY.established },
    { Icon: Users, label: 'Trading partners', value: '39,000+' },
    { Icon: MapPin, label: 'Headquarters', value: 'Ann Arbor, MI' },
  ];

  return (
    <>
      <PageHero
        eyebrow="About"
        title="EDI built by"
        titleAccent="EDI veterans."
        breadcrumbs={[{ label: 'About' }]}
        italicLine={COMPANY.tagline}
      />

      <section className="py-20 lg:py-24 bg-bg-canvas">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-7">
              <p className="eyebrow">Our Story</p>
              <h2 className="font-display font-bold text-3xl lg:text-4xl text-text-primary mb-6 tracking-[-0.02em] text-balance">
                Connecting manufacturers, distributors, and retailers since {COMPANY.established}.
              </h2>
              <div className="space-y-5 text-lg text-text-secondary leading-relaxed">
                <p>
                  Established in {COMPANY.established} in {COMPANY.address.city}, Michigan, iConnect is a technology service provider with deep expertise in EDI and trading partner integration - built for the way modern manufacturers, distributors, and retailers actually do business.
                </p>
                <p>
                  We're run by people who built the EDI industry: former leaders from Harbinger, Premenos, Sterling Commerce, and Supply Tech. That experience shows in everything we ship - from <Link to="/solutions/iexchangeweb" className="text-accent-primary hover:text-accent-bright font-medium">iExchangeWeb</Link>, our web-EDI platform, to our fully <Link to="/solutions/managed-edi" className="text-accent-primary hover:text-accent-bright font-medium">Managed EDI Services</Link> and Tier 1 <Link to="/solutions/van" className="text-accent-primary hover:text-accent-bright font-medium">Value Added Network</Link>.
                </p>
                <p>
                  Today we process tens of thousands of EDI documents every day for some of the most demanding supply chains on the planet - and we still answer the phone 24/7.
                </p>
              </div>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/about/management" className="btn-primary">
                  Meet the team
                  <ArrowRight size={16} />
                </Link>
                <Link to="/about/careers" className="btn-secondary">
                  Careers
                </Link>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="card-elevated p-8 lg:p-10 sticky top-28">
                <p className="eyebrow">By the numbers</p>
                <div className="space-y-6 mt-6">
                  {stats.map((s) => (
                    <div
                      key={s.label}
                      className="flex items-center gap-4 pb-6 border-b border-border-subtle last:border-0 last:pb-0"
                    >
                      <div className="w-12 h-12 rounded-xl bg-accent-soft flex items-center justify-center flex-shrink-0">
                        <s.Icon size={20} className="text-accent-primary" />
                      </div>
                      <div>
                        <div className="font-display font-bold text-2xl text-text-primary leading-none">
                          {s.value}
                        </div>
                        <div className="text-sm text-text-muted">{s.label}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <CTASection />
    </>
  );
};

export default About;
